import { useQuery } from '@tanstack/react-query';
import { CertificationPickerQuery, CertificationPickerQueryVariables } from '__generated__/graphql';
import classNames from 'classnames';
import DropDownPicker from 'components/foundation/pickers/DropDownPicker/DropDownPicker';
import SimplePickerRow from 'components/foundation/pickers/SimplePickerRow/SimplePickerRow';
import gql from 'graphql-tag';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import { find, first } from 'lodash';
import * as React from 'react';
import { ReactNode, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { useFeatureFlags } from 'utils/features';

export interface ICertificationPickerProps {
  value: string | null;
  onSelect: (certificationId: string | null) => void;
  minWidth?: string;
  placeholder?: string;
  error?: boolean;
  className?: ClassName;
  dropDownButtonRender?: ReactNode;
  closeButtonRender?: (handleClear: (event: React.KeyboardEvent | React.MouseEvent) => void) => ReactNode;
}

type CertificationPickerCertification = NonNullArrayField<CertificationPickerQuery['certifications']>;

const CertificationPicker = (props: ICertificationPickerProps) => {
  const {
    value,
    onSelect,
    minWidth = '350px',
    error,
    placeholder,
    closeButtonRender,
    dropDownButtonRender,
    className,
  } = props;

  const isCertificationStatusEnabled = useFeatureFlags('CERTIFICATIONS');

  const graphqlClient = useGraphQLClient();

  const [isOpen, setIsOpen] = useState(false);

  function onClose() {
    setIsOpen(false);
  }

  function handleSelect(model: CertificationPickerCertification | null) {
    onSelect(model ? model.id : null);
  }

  const baseQueryKey = useReactQueryBaseKey();

  const { data: selectedCertification } = useQuery({
    queryKey: [...baseQueryKey, 'certification-by-id', value],
    queryFn: async () => {
      if (!value) {
        return null;
      }

      const certification = find(data, (model) => model.id === value);

      if (certification) {
        return certification.name;
      } else {
        const result = await graphqlClient.request<CertificationPickerQuery, CertificationPickerQueryVariables>({
          document: CERTIFICATION_PICKER_QUERY,
          variables: {
            filter: { id: { equal: value } },
          },
        });

        const target = first(result?.certifications);
        return target?.name ?? null;
      }
    },
    initialData: null,
    enabled: !!value,
  });

  const variables: CertificationPickerQueryVariables = {
    filter: {
      deletedOn: { isNull: true },
    },
  };

  async function getCertifications(after: string | null, first: number): Promise<CertificationPickerCertification[]> {
    const pagedVariables: CertificationPickerQueryVariables = {
      after,
      first,
      ...variables,
    };

    const result = await graphqlClient.request<CertificationPickerQuery, CertificationPickerQueryVariables>({
      document: CERTIFICATION_PICKER_QUERY,
      variables: pagedVariables,
    });

    return result.certifications ?? [];
  }

  function renderValueTemplate() {
    if (selectedCertification) {
      return <>{selectedCertification}</>;
    }

    return <></>;
  }

  function renderRow(certification: CertificationPickerCertification) {
    return <SimplePickerRow value={certification.name} />;
  }

  const scroller = React.useRef<HTMLElement>();

  const {
    data,
    isLoading,
    isError: isDataError,
    loadedAll,
  } = useReactQueryLazyLoading(
    scroller.current,
    [...baseQueryKey, 'certification-picker', variables],
    getCertifications,
    10,
    {
      enabled: isOpen,
    }
  );

  const classes = classNames('certification-picker', className);

  if (!isCertificationStatusEnabled) {
    return null;
  }

  return (
    <DropDownPicker
      value={value}
      dataError={isDataError}
      error={error}
      data={data ?? []}
      loadedAll={loadedAll}
      onSelect={handleSelect}
      onClose={onClose}
      onOpen={() => setIsOpen(true)}
      valueTemplate={renderValueTemplate()}
      renderRow={renderRow}
      minWidth={minWidth}
      placeholder={placeholder}
      className={classes}
      closeButtonRender={closeButtonRender}
      dropDownButtonRender={dropDownButtonRender}
      setScrollerRef={(scrollerRef) => (scroller.current = scrollerRef)}
      isLoading={isLoading}
      disabled={false}
    />
  );
};

export default CertificationPicker;

export const CERTIFICATION_PICKER_QUERY = gql`
  query CertificationPicker($after: String, $first: Int, $filter: CertificationFilter) {
    certifications(after: $after, first: $first, filter: $filter) {
      id
      cursor
      name
      description
      createdOn
      updatedOn
      deletedOn
    }
  }
`;
